import CommentPanel from '@/components/edit/components/CommentPanel'
import EditorTheme from '@/components/edit/editor-theme'
import '@/components/edit/editor-theme.css'
import CommentPlugin from '@/components/edit/plugins/CommentPlugin'
import FloatingToolbarPlugin from '@/components/edit/plugins/FloatingToolbarPlugin'
import { SuggestionPlugin } from '@/components/edit/plugins/SuggestionPlugin'
import { ReviewTextPlugin } from '@/components/edit/review/LexicalReviewTextPlugin'
import '@/components/edit/styles/comments.css'
import '@/components/edit/styles/suggestions.css'
import '@/components/edit/styles/track-changes.css'
import ToolbarPlugin from '@/components/edit/toolbar-plugin'
import {
  assignBlockIdsToEditorState,
  getEditorNodes,
  parseHtmlToLexicalJsonState,
  cleanEditorState,
  handleApplySingleImprovement,
  selectTextInBlock,
} from '@/components/edit/utils'
import ReviewFeedbackModal from '@/components/feedback'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import LoadingIndicator from '@/components/ui/loading-indicator'
import { usePermanentStore } from '@/contexts/permanentStore'
import { AuthState } from '@/contexts/slices/auth'
import useAppState from '@/contexts/state'
import { ProductMetadata, useTemporaryStore } from '@/contexts/temporaryStore'
import { useBilling } from '@/hooks/useBilling'
import { eventsService } from '@/services/analytics/events-service'
import withRouter from '@components/common/withRouter'
import RiskAnalysisStreamingStatus, {
  RiskAnalysisStreamingStatusRef,
} from '@components/review/RiskAnalysisStreamingStatus'
import { OutputData } from '@editorjs/editorjs'
import { Icon } from '@iconify/react'
import { EngineAiOutput } from '@interfaces/ai'
import { DocumentResponse, IDocument } from '@interfaces/document'
import { SubscriptionStatus } from '@interfaces/auth'
import { EventCategory, EventName } from '@interfaces/events'
import { ExtendedSerializedEditorState } from '@interfaces/lexical'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { ai } from '@services/ai/ai-service'
import { docs } from '@services/docs/docs-service'
import { CLEAR_HISTORY_COMMAND, LexicalEditor } from 'lexical'
import { ChevronLeft, CloudCheck, CloudOff, LoaderCircle, MessageSquare, Save, Send, TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { IndentLimitPlugin } from '@/components/edit/plugins/IndentLimitPlugin'
import SendDocumentModal from '@/components/review/send-document-modal'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import RiskAnalysisResults from '@/components/review/RiskAnalysisResults'
import { BillingModalCopyKey } from '@/contexts/billing'

const Review = (props: any) => {
  const navigate = useNavigate()
  const { id } = props.router.params
  const { active, onSubscribe, subscription, updateSubscriptionMetadata } = useBilling()
  const headerExtraHeight = subscription?.status === SubscriptionStatus.Active ? '0rem' : '3rem'
  const subscriptionMetadata = (subscription?.metadata || {}) as ProductMetadata

  const [loading, setLoading] = useState(true)
  const [currentDocument, setCurrentDocument] = useState<IDocument>()
  const [documentVersionNumber, setDocumentVersionNumber] = useState<number>(0)
  const [dataOrHtml, setDataOrHtml] = useState<OutputData | string>()
  const [savedEditorState, setSavedEditorState] = useState<ExtendedSerializedEditorState | null>(null)
  const [blocksOrderData, setBlocksOrderData] = useState<string[]>([])
  const [isDocSaved, setIsDocSaved] = useState<boolean>(false)
  const [isSendDocumentModalOpen, setIsSendDocumentModalOpen] = useState(false)

  const editorRef = useRef<LexicalEditor | undefined>()
  const riskStatusRef = useRef<RiskAnalysisStreamingStatusRef>(null)

  // Helper function to get document display name (without version)
  const getDocumentDisplayName = () => {
    return currentDocument?.file_name || ''
  }

  document.title =
    documentVersionNumber > 0
      ? `${getDocumentDisplayName()} (V${documentVersionNumber})`
      : getDocumentDisplayName() || 'Review'

  // HANDLERS ------------------------------------------------------------

  const handleDocumentNameUpdate = (newName: string) => {
    setCurrentDocument(prev => (prev ? { ...prev, file_name: newName } : prev))
  }

  const { user } = useAppState(s => ({
    user: (s as AuthState).user,
  }))

  const {
    editingStartTime,
    setEditingStartTime,
    docsOpened,
    addDocsOpened,
    setCurrentDocumentTemplateData,
    currentDocumentMetadata,
    setCurrentDocumentMetadata,
    setReviewMetadata,
    addActiveProcessingBlock,
    removeActiveProcessingBlock,
    productMetadata,
  } = useTemporaryStore(s => ({
    editingStartTime: s.editingStartTime,
    setEditingStartTime: s.setEditingStartTime,
    docsOpened: s.docsOpened,
    addDocsOpened: s.addDocsOpened,
    setCurrentDocumentTemplateData: s.setCurrentDocumentTemplateData,
    currentDocumentMetadata: s.currentDocumentMetadata,
    setCurrentDocumentMetadata: s.setCurrentDocumentMetadata,
    productMetadata: s.productMetadata,
    setReviewMetadata: s.setReviewMetadata,
    addActiveProcessingBlock: s.addActiveProcessingBlock,
    removeActiveProcessingBlock: s.removeActiveProcessingBlock,
  }))

  const { setAutoFeedbackModalTimerStart } = usePermanentStore(s => ({
    setAutoFeedbackModalTimerStart: s.setAutoFeedbackModalTimerStart,
  }))

  const [reviewInProgress, setReviewInProgress] = useState<boolean>(false)
  const [aiOutput, setAiOutput] = useState<EngineAiOutput>()
  const [hasPendingSuggestions, setHasPendingSuggestions] = useState<boolean>(false)
  const [reviewCompleted, setReviewCompleted] = useState<boolean>(false)
  const [riskAnalysisStreamingStatus, setRiskAnalysisStreamingStatus] = useState<[string, Record<string, any>][]>([])

  // Panel visibility states
  const [isCommentPanelOpen, setIsCommentPanelOpen] = useState<boolean>(false)
  const [isReviewPanelOpen, setIsReviewPanelOpen] = useState<boolean>(false)

  // Track changes state
  const [isTrackChangesEnabled, setIsTrackChangesEnabled] = useState<boolean>(false)

  // Track unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const [showUnsavedAnimation, setShowUnsavedAnimation] = useState<boolean>(false)

  // FUNCTIONS ------------------------------------------------------------
  const checkImprovementsLeftToApply = useCallback(() => {
    if (!aiOutput) return false

    // Check if all improvements have Applied: true
    return !aiOutput.riskCards.every(improvement => improvement.status === 'APPLIED')
  }, [aiOutput])

  async function handleApplyAllImprovements() {
    if (!editorRef.current || !aiOutput) return

    // trial limit check
    const isTrialOrInactive = subscription?.status !== SubscriptionStatus.Active
    if (isTrialOrInactive) {
      const trialAiImprovementCap = productMetadata?.trialAiImprovementCap ?? 6
      const aiImprovementCount = currentDocumentMetadata?.aiImprovementCount ?? 0

      if (aiImprovementCount >= trialAiImprovementCap) {
        onSubscribe('TRIAL_LIMIT_AI_IMPROVEMENTS')
        return
      }
    }

    if (aiOutput.riskCards.length === 0) {
      toast.error('No improvements to apply')
      return
    }

    // Check if test mode is activated
    const url = new URL(window.location.href)
    const testMode = url.searchParams.get('test') === 'true'
    // --------------------------------------------

    toast.loading('Applying all improvements...', { duration: 3000 })

    // find the apply all improvements button => to dynamically update the text of the button
    const applyAllImprovementsBtn = document.getElementById('apply-all-improvements-btn') as HTMLButtonElement
    if (!applyAllImprovementsBtn) return toast.error('No improvements to apply')
    applyAllImprovementsBtn.disabled = true
    applyAllImprovementsBtn.textContent = 'Applying...'

    // filter the ai output to only include the risks that are not applied and exclude informational cards (riskFactor === 1)
    let risksToApply = aiOutput.riskCards.filter(risk => risk.status !== 'APPLIED' && risk.riskFactor >= 2)

    // risksToApply = risksToApply.slice(0, 1)

    // if there are multiple risks for one single blockId, get the one with highest risk factor
    if (risksToApply && risksToApply.length > 0) {
      const blockIdMap = new Map<string, EngineAiOutput['riskCards'][number]>()

      risksToApply.forEach(risk => {
        const existing = blockIdMap.get(risk.blockId)
        if (!existing || risk.riskFactor > existing.riskFactor) {
          blockIdMap.set(risk.blockId, risk)
        }
      })

      risksToApply = Array.from(blockIdMap.values())
    }

    // if no risks to apply, show a toast and return
    if (!risksToApply || risksToApply.length === 0) {
      toast.error('No improvements to apply')
      applyAllImprovementsBtn.textContent = 'Apply all improvements'
      applyAllImprovementsBtn.disabled = false
      return
    }

    applyAllImprovementsBtn.textContent = `Applying 0/${risksToApply.length}`

    // Helper function to apply a single risk improvement
    const applySingleRisk = async (risk: EngineAiOutput['riskCards'][number]) => {
      if (!editorRef.current || !aiOutput) {
        return { success: false, error: 'Editor or AI output not available' }
      }

      selectTextInBlock(editorRef.current, risk.blockId)

      // Scroll the risk card into view
      const riskCardElement = document.querySelector(`[data-risk-card-id="${risk.blockId}"]`)
      if (riskCardElement) {
        riskCardElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      // Scroll the editor block into view
      const editorBlockElement = document.querySelector(`[data-block-id="${risk.blockId}"]`)
      if (editorBlockElement) {
        editorBlockElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // wait for some time to ensure the block is scrolled into view
        await new Promise(resolve => setTimeout(resolve, 1000))

        addActiveProcessingBlock(risk.blockId, 'Applying improvement...')
      }

      let result: { success: boolean; error: string | { errorCode?: BillingModalCopyKey } | null } = {
        success: false,
        error: null,
      }

      await handleApplySingleImprovement({
        editor: editorRef.current!,
        risk,
        documentVersionNumber,
        documentPublicId: id || '',
        testMode,
        documentSummary: aiOutput!.documentSummary,
        onStreamStart: () => {
          // Stream started
        },
        onTextChunk: (_text: string) => {
          // Clear processing indicator once we start receiving text
          removeActiveProcessingBlock(risk.blockId)
        },
        onStreamEnd: (finalText: string) => {
          if (finalText) {
            // Update the aiOutput state to mark this risk as applied
            setAiOutput(prev => {
              if (!prev) return prev
              return {
                ...prev,
                riskCards: prev.riskCards.map(r =>
                  r.blockId === risk.blockId &&
                  r.suggestion.toLowerCase().slice(0, 50).trim() === risk.suggestion.toLowerCase().slice(0, 50).trim()
                    ? { ...r, appliedImprovement: finalText, status: 'APPLIED' }
                    : r
                ),
              }
            })
            result = { success: true, error: null }
          } else {
            console.error(`Empty response received for risk ${risk.blockId}`)
            result = { success: false, error: 'Empty response received' }
          }
        },
        onStreamError: (error: string | { errorCode?: BillingModalCopyKey }) => {
          removeActiveProcessingBlock(risk.blockId)
          result = { success: false, error }
        },
      }).catch((error: any) => {
        removeActiveProcessingBlock(risk.blockId)
        result = { success: false, error }
      })

      return result
    }

    // Apply all improvements
    const failedRisks: EngineAiOutput['riskCards'][number][] = []
    let successCount = 0

    for (let i = 0; i < risksToApply.length; i++) {
      const risk = risksToApply[i]
      const result = await applySingleRisk(risk)

      if (result.success) {
        successCount++
        applyAllImprovementsBtn.textContent = `Applying ${successCount}/${risksToApply.length}`
      } else {
        if (result.error && typeof result.error === 'object' && result.error?.errorCode) {
          applyAllImprovementsBtn.textContent = 'Apply all improvements'
          applyAllImprovementsBtn.disabled = false
          onSubscribe(result.error.errorCode as BillingModalCopyKey)
          toast.error(
            `Looks like you have used all your ${productMetadata?.trialAiImprovementCap ?? 6} free improvements.`
          )
          return
        }

        failedRisks.push(risk)
      }
    }

    // Retry failed improvements once
    if (failedRisks.length > 0) {
      toast.loading('Retrying some failed improvements...', { duration: 6000 })

      for (let i = 0; i < failedRisks.length; i++) {
        const risk = failedRisks[i]
        const result = await applySingleRisk(risk)

        if (result.success) {
          successCount++
        }
      }
    }

    // Final summary
    applyAllImprovementsBtn.textContent = 'Applied all improvements!'
    applyAllImprovementsBtn.disabled = false

    if (successCount === 0) {
      toast.error('Failed to apply improvements. Please try again.')
    } else {
      toast.success(`${successCount} improvement(s) successfully applied!`)
      setAutoFeedbackModalTimerStart(new Date())
    }
  }

  const handleReviewError = () => {
    setReviewInProgress(false)
    setRiskAnalysisStreamingStatus([])

    if (!aiOutput || aiOutput.riskCards.length === 0) setIsReviewPanelOpen(false)
  }

  const startReview = async (args?: { performFullReview: boolean }) => {
    // Check review cap for trial users before starting
    const isTrialOrInactive = subscription?.status !== SubscriptionStatus.Active
    if (isTrialOrInactive) {
      const trialReviewsCap = productMetadata?.trialReviewsCap ?? 5
      const reviewsCount = subscriptionMetadata?.reviewsCount ?? 0

      if (reviewsCount >= trialReviewsCap) {
        onSubscribe('TRIAL_LIMIT_REVIEWS_COUNT')
        return
      }
    }

    setReviewInProgress(true)
    setIsReviewPanelOpen(true)

    // Reset child component state before starting new review
    riskStatusRef.current?.reset()
    setRiskAnalysisStreamingStatus([['saving_document', {}]])

    if (hasUnsavedChanges) await handleSaveFile()

    setRiskAnalysisStreamingStatus(prev => [...prev, ['connecting_to_engine', {}]])

    // Check if test mode is activated
    const url = new URL(window.location.href)
    const testMode = url.searchParams.get('test') === 'true'
    // --------------------------------------------

    ai.review(
      id,
      testMode,
      args?.performFullReview ?? false,
      handleStreamEvent,
      (error: { error: string; errorCode?: BillingModalCopyKey } | string) => {
        if (typeof error === 'object' && error.errorCode) {
          onSubscribe(error.errorCode)
          handleReviewError()
          return
        }

        toast.error(
          typeof error === 'string'
            ? error
            : error?.error || 'Something went wrong while reviewing the document. Please try again.'
        )
        handleReviewError()
      }
    )
  }

  function handleStreamEvent(event: string, data: unknown) {
    // ping event is sent by the ai engine to the backend, and proxied to the frontend, to keep both streaming connections alive
    if (event === 'ping') return

    setRiskAnalysisStreamingStatus(prev => [...prev, [event, data as Record<string, any>]])

    if (event === 'error') {
      const payload = data as {
        error: string
      }

      // resume the already ongoing review
      if (payload.error.includes('ANOTHER_REVIEW_IN_PROGRESS')) {
        riskStatusRef.current?.reset()
        checkForActiveReview()
        return
      }

      toast.error(payload.error ?? 'Something went wrong while reviewing the document. Please try again.')
      handleReviewError()
    } else if (event === 'stream_start') {
      setAiOutput(undefined)
      setReviewCompleted(false)
    } else if (event === 'stream_end') {
      const payload = data as {
        aiOutputFrontendExpected?: EngineAiOutput
        documentVersionNumber: number
        metadata: any
      }

      setReviewInProgress(false)
      setReviewCompleted(true)

      // stream ends without any ai output (eg. parties detected) => do nothing
      if (payload?.aiOutputFrontendExpected == null) {
        return
      }

      // Ensure we create a new reference for the riskCards array to force re-render
      setAiOutput({
        ...payload.aiOutputFrontendExpected,
        riskCards: [...payload.aiOutputFrontendExpected.riskCards],
      })

      if (payload.documentVersionNumber) {
        setDocumentVersionNumber(payload.documentVersionNumber)
      }

      setAutoFeedbackModalTimerStart(new Date())

      toast.success('Review completed!')

      // Increment reviews count in subscription metadata for trial users to keep frontend in sync
      const isTrialOrInactive = subscription?.status !== SubscriptionStatus.Active
      if (isTrialOrInactive) {
        const currentReviewsCount = subscriptionMetadata.reviewsCount ?? 0
        updateSubscriptionMetadata({ reviewsCount: currentReviewsCount + 1 })
      }

      if (window.location.hostname.includes('localhost') || window.location.hostname.includes('staging.')) {
        console.log(payload.metadata)
        setReviewMetadata(payload.metadata)
      }
    } else if (event === 'block_analyzed') {
      const payload = data as {
        riskCards: EngineAiOutput['riskCards']
      }

      setAiOutput(prev => {
        if (!prev) {
          return {
            documentSummary: '',
            documentType: 'generic',
            riskCards: payload.riskCards,
          }
        }

        return {
          ...prev,
          riskCards: [...prev.riskCards, ...payload.riskCards],
        }
      })
    } else if (event === 'preprocessing_document_end') {
      const payload = data as {
        documentSummary: string
        documentType: EngineAiOutput['documentType']
      }

      setAiOutput(prev => {
        if (!prev) {
          return {
            documentSummary: payload.documentSummary,
            documentType: payload.documentType,
            riskCards: [],
          }
        }

        return {
          ...prev,
          documentSummary: payload.documentSummary,
        }
      })
    }
  }

  const checkForActiveReview = async () => {
    fetch(`/api/ai/review/${id}/resume`, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
      },
    })
      .then(async response => {
        // If not SSE or no active job, response might be JSON
        const contentType = response.headers.get('content-type')
        if (!contentType?.includes('text/event-stream')) {
          const jsonData = (await response.json()) as { active: boolean }
          if (!jsonData.active) {
            // No active review to resume
            return
          }
        }

        if (!response.body) return

        // Active review found! Lock the button
        setReviewInProgress(true)
        setAiOutput(undefined)
        setReviewCompleted(false)

        setRiskAnalysisStreamingStatus([['saving_document', {}]])
        setRiskAnalysisStreamingStatus(prev => [...prev, ['connecting_to_engine', {}]])

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const events = buffer.split('\n\n')
            buffer = events.pop() || ''

            for (const eventBlock of events) {
              if (!eventBlock.trim() || !eventBlock.startsWith('event: ')) continue

              const [eventLine, dataLine] = eventBlock.split('\n')
              if (eventLine?.startsWith('event: ') && dataLine?.startsWith('data: ')) {
                const event = eventLine.substring(7)
                const data = JSON.parse(dataLine.substring(6))

                // Handle different event types
                if (event === 'replay_events' || event === 'new_events') {
                  // Replay all stored events
                  const incomingEvents = data.events || []
                  incomingEvents.forEach(([eventName, eventData]: [string, object]) => {
                    handleStreamEvent(eventName, eventData)
                  })
                } else if (event === 'stream_complete') {
                  break
                } else if (event === 'error') {
                  handleReviewError()
                  break
                }
              }
            }
          }
        } catch (error: any) {
          console.error('Resume stream error:', error)
        }
      })
      .catch(() => {
        setReviewInProgress(false)
        setReviewCompleted(false)
      })
  }

  const loadDocument = async () => {
    try {
      setLoading(true)

      const documentResponse = await docs.getContentsByPublicId(id)
      const { data, files, versionNumber, versionParties } = documentResponse.data as DocumentResponse
      setDocumentVersionNumber(versionNumber)
      const { processing_status, editor_state } = data

      setSavedEditorState(editor_state)
      editor_state && setBlocksOrderData(createBlockOrderData(editor_state))
      setCurrentDocument(data)
      setCurrentDocumentTemplateData(data.template_data)
      setCurrentDocumentMetadata(data.metadata || null)
      setDataOrHtml((files?.[0]?.data as string) || 'Start writing your document ...')
      setIsReviewPanelOpen(data.metadata?.reviewPanelOpen ?? true)

      if (Array.isArray(data.ai_output?.riskCards)) {
        setAiOutput(data.ai_output)
        setReviewCompleted(true)

        // mimic a state that the review is completed
        setRiskAnalysisStreamingStatus([['stream_end', { versionParties }]])
      }

      if (processing_status === 'failed_conversion') {
        toast.error('Document conversion failed. Please try again.')
        return true
      }

      // Check for active review jobs after document loads successfully
      await checkForActiveReview()
    } catch (error: any) {
      if (error?.name === 'AxiosError' && error?.status === 404) {
        navigate('/dashboard', { replace: true })
        toast.error('This document does not seem to exist.')
        return
      }

      toast.error(
        error?.data?.message ?? 'An unknown error has occurred, please check again later or contact support.',
        {
          duration: 5000,
        }
      )
    } finally {
      setLoading(false)
    }
  }

  function createBlockOrderData(jsonState: ExtendedSerializedEditorState) {
    if (!jsonState) return []

    return jsonState.root.children.map(block => block.__blockId)
  }

  const handleSaveFile = async () => {
    if (!editorRef.current) return

    // fire save_attempted event
    eventsService.fireEvent({
      name: EventName.SAVE_ATTEMPTED,
      category: EventCategory.CORE_BEHAVIOR,
      doc_public_id: id,
      source: 'SAVE_BUTTON',
      meta: {},
    })

    // fire doc_saved_multiple_times event
    if (isDocSaved) {
      eventsService.fireEvent({
        name: EventName.DOC_SAVED_MULTIPLE_TIMES,
        category: EventCategory.RETURN_BEHAVIOR,
        doc_public_id: id,
        source: 'SAVE_BUTTON',
        meta: {},
      })
    } else {
      setIsDocSaved(true)
    }

    // get the current editor state and assign block ids to newly added blocks
    const state = editorRef.current?.getEditorState().toJSON()
    assignBlockIdsToEditorState(state as ExtendedSerializedEditorState)

    // we should update the editor state to contain the block ids, otherwise auto scroll to block doesn't work
    const parsedEditorState = editorRef.current.parseEditorState(state as ExtendedSerializedEditorState)
    editorRef.current.setEditorState(parsedEditorState)

    await docs
      .updateEditorState(id, state)
      .then(() => {
        // Clear unsaved changes flag after saving
        setHasUnsavedChanges(false)

        // fire save_successful event
        eventsService.fireEvent({
          name: EventName.SAVE_SUCCESSFUL,
          category: EventCategory.CORE_BEHAVIOR,
          doc_public_id: id,
          source: 'SAVE_BUTTON',
          meta: {},
        })

        toast.success('All document changes are saved')
      })
      .catch(() => {
        toast.error("We couldn't save the document. Please try again.")
      })
  }

  // EFFECTS ------------------------------------------------------------

  useEffect(() => {
    loadDocument()

    return () => {
      setCurrentDocumentTemplateData(null)
      setCurrentDocumentMetadata(null)
      setReviewMetadata(null)
    }
  }, [])

  // This runs when the editor is mounted & ready
  useEffect(() => {
    if (!editorRef.current) return

    // Clear history after loading saved state
    editorRef.current.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined)

    // fire editor_loaded event
    eventsService.fireEvent({
      name: EventName.EDITOR_LOADED,
      category: EventCategory.CORE_BEHAVIOR,
      doc_public_id: id,
      source: undefined,
      meta: {},
    })

    // fire multiple_docs_created event
    addDocsOpened(id)
    const tempDocsOpened = Array.from(new Set([...docsOpened, id]))
    if (tempDocsOpened.length > 1) {
      eventsService.fireEvent({
        name: EventName.MULTIPLE_DOCS_CREATED,
        category: EventCategory.RETURN_BEHAVIOR,
        doc_public_id: id,
        source: undefined,
        meta: {
          unique_doc_count: tempDocsOpened.length,
        },
      })
    }

    // If there is a saved editor state, use it
    if (savedEditorState) {
      // if not active, redirect to /readonly page
      if (!active) {
        navigate(`/readonly?id=${id}`, { replace: true })
        return
      }

      // Clean malformed link nodes before parsing
      const cleanedEditorState = cleanEditorState(savedEditorState)
      const parsedEditorState = editorRef.current.parseEditorState(cleanedEditorState)
      editorRef.current.setEditorState(parsedEditorState)

      // fire doc_reopened_by_user event
      eventsService.fireEvent({
        name: EventName.DOC_REOPENED_BY_USER,
        category: EventCategory.RETURN_BEHAVIOR,
        doc_public_id: id,
        source: undefined,
        meta: {},
      })

      return
    }

    // If there is no saved editor state, get the raw html file, load it into the editor and send it to the server to store in the database
    if (!dataOrHtml || typeof dataOrHtml !== 'string') return

    // wait for location.reload() below to trigger
    if (currentDocument?.blocks) setLoading(true)

    const editorJsonState = parseHtmlToLexicalJsonState(dataOrHtml)
    setSavedEditorState(editorJsonState)
    setBlocksOrderData(createBlockOrderData(editorJsonState))

    const parsedEditorState = editorRef.current.parseEditorState(editorJsonState)
    editorRef.current.setEditorState(parsedEditorState)

    if (editorJsonState) {
      docs.setInitialEditorState(id, { editorState: editorJsonState }).then(() => {
        // if not active, redirect to /readonly page
        if (!active) {
          navigate(`/readonly?id=${id}`, { replace: true })
          return
        }

        // initial version is created by this api call
        setDocumentVersionNumber(1)

        // for backwards compatibility with old documents parsed using editorJs, probably remove in the future
        if (currentDocument?.blocks) location.reload()
      })
    }
  }, [dataOrHtml, savedEditorState])

  // Track unsaved changes by listening to editor state changes
  useEffect(() => {
    if (editorRef.current) {
      const unregister = editorRef.current.registerUpdateListener(({ dirtyElements, dirtyLeaves }) => {
        // Only mark as having unsaved changes if there are actual dirty elements/leaves
        if (dirtyElements.size > 0 || dirtyLeaves.size > 0) {
          setHasUnsavedChanges(true)

          // need these for review feedback object
          if (editingStartTime == null) {
            setEditingStartTime(Date.now())
          }

          setAutoFeedbackModalTimerStart(new Date())

          // fire editor_edited event (based on session storage)
          const firstEditMade = sessionStorage.getItem(`first_edit_made`)

          if (!firstEditMade) {
            eventsService.fireEvent({
              name: EventName.EDITOR_EDITED,
              category: EventCategory.CORE_BEHAVIOR,
              doc_public_id: id,
              source: undefined,
              meta: {},
            })

            sessionStorage.setItem(`first_edit_made`, 'true')
          }
        }
      })

      return () => {
        unregister()
      }
    }
  }, [editorRef.current, editingStartTime])

  // Handle unsaved changes animation
  useEffect(() => {
    if (hasUnsavedChanges && !showUnsavedAnimation) {
      const timer = setTimeout(() => {
        setShowUnsavedAnimation(false)
      }, 2000) // Animation duration: 2 seconds

      return () => clearTimeout(timer)
    }
  }, [hasUnsavedChanges, showUnsavedAnimation])

  // Warn about unsaved changes when attempting to leave the page
  // I think only IE supports custom messages, so the user will probably see the standard popup
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const message = 'You have unsaved changes. Are you sure you want to leave?'
        e.preventDefault()
        e.returnValue = message
        return message
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  // RENDER ------------------------------------------------------------
  return loading || !currentDocument || !dataOrHtml ? (
    <LoadingIndicator label="Loading Document" />
  ) : (
    <>
      <LexicalComposer
        initialConfig={{
          namespace: 'RichTextEditor',
          theme: EditorTheme,
          editable: true,
          onError: (error: Error) => {
            console.error('Editor error:', error)
          },
          nodes: getEditorNodes(),
        }}
      >
        <div
          className="sticky z-50 w-full bg-white dark:bg-gray-950"
          style={{ top: `calc(4rem + ${headerExtraHeight})` }}
        >
          {/* second navbar under the main app bar */}
          <header className="flex h-16 w-full items-center justify-between border-b bg-white px-4 md:px-6">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="cursor-pointer rounded-md p-2 transition-colors duration-150 hover:bg-gray-100"
              >
                <ChevronLeft className="h-5 w-5" />
              </Link>

              <EditableDocumentName
                documentName={getDocumentDisplayName()}
                documentVersionNumber={documentVersionNumber}
                publicId={id}
                onNameUpdate={handleDocumentNameUpdate}
              />
            </div>

            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-sm text-default-600">
                {hasUnsavedChanges ? (
                  <CloudOff className="h-4 w-4 stroke-[2.5]" />
                ) : (
                  <CloudCheck className="h-4 w-4 stroke-[2.5]" />
                )}
                <span>{!hasUnsavedChanges ? 'All changes are saved' : 'Unsaved changes'}</span>
              </div>

              <Button
                disabled={!hasUnsavedChanges}
                onClick={handleSaveFile}
                variant="light"
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4 stroke-[2.5]" />
                Save
              </Button>

              <Button
                variant="light"
                className="flex items-center gap-2"
                onClick={() => {
                  if (hasUnsavedChanges) handleSaveFile()
                  setIsSendDocumentModalOpen(true)
                }}
              >
                <Send className="h-4 w-4 stroke-[2.5]" />
                Send
              </Button>
            </div>
          </header>

          {/* third navbar for editor buttons */}
          <header className="flex h-16 w-full items-center justify-between border-b bg-white px-4 md:px-6">
            <div className="mx-auto">
              <ToolbarPlugin
                onSaveFile={handleSaveFile}
                isTrackChangesEnabled={isTrackChangesEnabled}
                setIsTrackChangesEnabled={setIsTrackChangesEnabled}
                setHasPendingSuggestions={setHasPendingSuggestions}
                documentName={getDocumentDisplayName()}
              />
            </div>

            {/* review button */}
            <div className="flex items-center gap-4">
              {!reviewInProgress ? (
                <>
                  {!reviewCompleted && !(isReviewPanelOpen && !aiOutput && riskAnalysisStreamingStatus.length === 0) ? (
                    <div className="flex items-center">
                      <Button
                        className="step-3"
                        disabled={hasPendingSuggestions}
                        variant="accentGreen"
                        onClick={() => startReview()}
                      >
                        Improve document
                      </Button>
                    </div>
                  ) : reviewCompleted ? (
                    <div className="flex items-center">
                      <Button
                        disabled={hasPendingSuggestions}
                        className="flex items-center gap-2"
                        variant="light"
                        onClick={() => startReview()}
                      >
                        Review changes
                        {subscription?.status !== SubscriptionStatus.Active && (
                          <span className="ml-1 text-xs opacity-70">
                            ({subscriptionMetadata.reviewsCount ?? 0}/{productMetadata?.trialReviewsCap ?? 5})
                          </span>
                        )}
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <Button variant="light" className="flex items-center gap-2">
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  Reviewing
                </Button>
              )}

              {/* apply all improvements button */}
              {aiOutput && (
                <Button
                  variant="accentGreen"
                  className="flex items-center gap-2"
                  onClick={handleApplyAllImprovements}
                  disabled={!checkImprovementsLeftToApply()}
                  id="apply-all-improvements-btn"
                >
                  <Icon icon="lucide:pen-line" className="h-5 w-5" />
                  Apply all improvements
                </Button>
              )}
            </div>
          </header>
        </div>

        <div className="relative grid grid-cols-12">
          <div
            className={cn(
              'p-5 transition-all duration-300',
              isReviewPanelOpen || isCommentPanelOpen ? 'col-span-8' : 'col-span-12'
            )}
          >
            <Card className="container flex min-h-screen max-w-5xl flex-col border p-5">
              <div className="relative flex min-h-[500px] flex-1">
                {/* Main Editor Area */}
                <div className="relative flex flex-1 flex-col">
                  <EditorRefPlugin editorRef={editorRef} />
                  <div className="relative flex-1 overflow-auto">
                    <RichTextPlugin
                      contentEditable={
                        <ContentEditable
                          className={`relative h-full min-h-[400px] flex-1 resize-none overflow-y-auto rounded-b-lg p-[15px] text-base caret-black outline-none [tab-size:1]`}
                        />
                      }
                      placeholder={
                        <div className="pointer-events-none absolute left-[15px] top-[15px] inline-block select-none overflow-hidden text-ellipsis text-base text-[#999]">
                          Import a document or begin writing
                        </div>
                      }
                      ErrorBoundary={LexicalErrorBoundary as any}
                    />
                    <HistoryPlugin />
                    <AutoFocusPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <TabIndentationPlugin />
                    <IndentLimitPlugin maxIndentLevel={5} />
                    <CommentPlugin
                      onAddComment={commentData => console.log('Comment added:', commentData)}
                      onUpdateComment={(commentId, commentData) =>
                        console.log('Comment updated:', commentId, commentData)
                      }
                      onResolveComment={commentId => console.log('Comment resolved:', commentId)}
                      onDeleteComment={(commentId: string) => console.log('Comment deleted:', commentId)}
                      sync={handleSaveFile}
                    />
                    <FloatingToolbarPlugin
                      currentUser={user?.email || 'Anonymous'}
                      showFindRisksBtn={aiOutput?.riskCards && aiOutput?.riskCards.length > 0}
                    />
                    <ReviewTextPlugin isEnabled={isTrackChangesEnabled} />
                    <SuggestionPlugin isEnabled={true} />
                  </div>
                </div>

                {/* Divider */}
                <div className="mx-1 w-px -translate-y-5 bg-gray-300" />

                {/* Toggle Buttons */}
                <div className="flex flex-col justify-start gap-2 pl-4">
                  <button
                    onClick={() => {
                      const newState = !isCommentPanelOpen
                      setIsCommentPanelOpen(newState)
                      if (newState) setIsReviewPanelOpen(false)
                    }}
                    className={`rounded-lg border p-2 transition-colors hover:bg-gray-50 ${
                      isCommentPanelOpen ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-600'
                    }`}
                    title="Toggle Comments Panel"
                  >
                    <MessageSquare className="h-5 w-5" />
                  </button>

                  <button
                    onClick={() => {
                      const newState = !isReviewPanelOpen
                      setIsReviewPanelOpen(newState)
                      if (newState) setIsCommentPanelOpen(false)

                      // Save preference to document metadata
                      docs.update(id, { metadata: { reviewPanelOpen: newState } })
                      setCurrentDocumentMetadata({ ...currentDocumentMetadata, reviewPanelOpen: newState })
                    }}
                    className={`rounded-lg border p-2 transition-colors hover:bg-gray-50 ${
                      isReviewPanelOpen ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-600'
                    }`}
                    title="Toggle Review Panel"
                  >
                    <TriangleAlert className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {isCommentPanelOpen && (
            <CommentPanel
              isOpen={isCommentPanelOpen}
              onClose={() => setIsCommentPanelOpen(false)}
              currentUser={user?.email || 'Anonymous'}
              onSave={handleSaveFile}
            />
          )}

          {isReviewPanelOpen && (
            <div
              className="sidebar sticky col-span-4 h-[calc(100vh-12rem)] overflow-y-auto border-l p-4 pb-10 pt-0"
              style={{ top: `calc(12rem + ${headerExtraHeight})` }}
            >
              {!aiOutput && riskAnalysisStreamingStatus.length === 0 ? (
                <div className="mx-auto flex max-w-[20rem] flex-col items-center justify-center py-10 text-center ">
                  <Button
                    className="mb-[1.5rem] text-[1rem]"
                    variant="accentGreen"
                    size={'lg'}
                    onClick={() => startReview()}
                  >
                    ✨ Improve this document
                  </Button>

                  <div className="mb-2">
                    Get instant feedback on clarity, tone, and missing points, powered by Cobrief AI.
                  </div>
                  {subscription?.status === SubscriptionStatus.Trialing && (
                    <div className="text-sm text-gray-500">Free during your trial.</div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <RiskAnalysisStreamingStatus
                    ref={riskStatusRef}
                    eventsArray={riskAnalysisStreamingStatus}
                    publicId={id}
                    onPartySelection={args => startReview(args)}
                  />

                  {aiOutput && (
                    <RiskAnalysisResults
                      key={reviewInProgress.toString()}
                      reviewInProgress={reviewInProgress}
                      blocksOrderData={blocksOrderData}
                      output={aiOutput}
                      versionNumber={documentVersionNumber}
                      setAiOutput={setAiOutput}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </LexicalComposer>

      <ReviewFeedbackModal />
      <SendDocumentModal
        isOpen={isSendDocumentModalOpen}
        onClose={() => setIsSendDocumentModalOpen(false)}
        versionNumber={documentVersionNumber}
        documentPublicId={id}
        documentName={getDocumentDisplayName()}
      />
    </>
  )
}

// Component for editable document name with version badge
const EditableDocumentName = ({
  documentName,
  documentVersionNumber,
  publicId,
  onNameUpdate,
}: {
  documentName: string
  documentVersionNumber: number
  publicId: string
  onNameUpdate: (newName: string) => void
}) => {
  const [isEditingName, setIsEditingName] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Helper component for version badge
  const VersionBadge = ({ version }: { version: number }) => {
    if (version === 0) return null
    return (
      <span className="ml-2 inline-flex items-center rounded-full bg-black px-2 py-1 text-xs font-medium text-white">
        V{version}
      </span>
    )
  }

  const handleStartEditingName = () => {
    setIsEditingName(true)
    // Focus the input after it renders
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.value = documentName
        nameInputRef.current.focus()
        nameInputRef.current.select()
      }
    }, 0)
  }

  const handleSaveDocumentName = async () => {
    const newName = nameInputRef.current?.value?.trim()

    if (!newName || newName === documentName) {
      setIsEditingName(false)
      return
    }

    try {
      await docs.update(publicId, { file_name: newName })
      onNameUpdate(newName)
      toast.success('Document name updated')
    } catch (error) {
      toast.error('Failed to update document name')
    } finally {
      setIsEditingName(false)
    }
  }

  const handleNameInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveDocumentName()
    } else if (e.key === 'Escape') {
      setIsEditingName(false)
    }
  }

  return (
    <>
      {isEditingName ? (
        <Input
          ref={nameInputRef}
          className="h-8"
          onBlur={handleSaveDocumentName}
          onKeyDown={handleNameInputKeyDown}
          placeholder="Document name"
        />
      ) : (
        <div
          className="cursor-pointer rounded px-2 py-1 text-sm font-medium transition-colors duration-150 hover:bg-gray-100"
          onClick={handleStartEditingName}
        >
          {documentName}
        </div>
      )}
      <VersionBadge version={documentVersionNumber} />
    </>
  )
}

export default withRouter(Review)
